import { FC } from "react";
import { Formik, FormikHelpers } from "formik";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useCreateReportAccessInformationCredentialsMutation } from "app/api/graph/types";
import { reportAccessInformationGraphId } from "app/api/graph/helpers";
import yup from "app/util/yup.ts";
import { StatusAlert } from "app/component/form/StatusAlert.tsx";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor.tsx";
import { handleError } from "app/api/graph/form.ts";
import { FormControlPassword } from "app/component/form/control/FormControlPassword";

type Props = {
    accessInformationId: number;
    onCreate?: () => void;
};

type FormValues = {
    username?: string;
    password?: string;
    url?: string;
    notes?: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    username: yup.string(),
    password: yup.string(),
    url: yup.string().url(),
    notes: yup.string(),
});

const initialValues: FormValues = {
    username: "",
    password: "",
    url: "",
    notes: "",
};

export const CreateReportAccessInformationCredentialsForm: FC<Props> = ({ accessInformationId, onCreate }) => {
    const [createAccessInformationCredentials] = useCreateReportAccessInformationCredentialsMutation({});

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        createAccessInformationCredentials({
            variables: {
                input: {
                    accessInformation: reportAccessInformationGraphId(accessInformationId),
                    username: values.username,
                    password: values.password,
                    url: values.url,
                    notes: values.notes,
                },
            },
        })
            .then((response) =>
                response.data?.createReportAccessInformationCredentials?.reportAccessInformationCredentials
                    ? onCreate?.()
                    : undefined,
            )
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({
                handleSubmit,
                setFieldValue,
                setFieldTouched,
                values,
                isSubmitting,
                errors,
                touched,
                status,
                handleChange,
                handleBlur,
            }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                            name="username"
                            value={values.username ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.username && !!errors.username}
                            autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <FormControlPassword
                            name="password"
                            placeholder="New password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && !!errors.password}
                        />
                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>URL</Form.Label>
                        <Form.Control
                            type="url"
                            name="url"
                            value={values.url ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.url && !!errors.url}
                            autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.url}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <RichTextEditor
                            value={values.notes ?? ""}
                            onChange={(value) => setFieldValue("notes", value)}
                            onBlur={() => setFieldTouched("notes")}
                            isInvalid={touched.notes && !!errors.notes}
                        />
                        <Form.Control.Feedback type="invalid">{errors.notes}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
